import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { t } from 'i18next';
import CalendarPicker from '../util/CalendarPicker';
import CategoryTaskPicker from '../timesheetsComp/CategoryTaskPicker';

interface Props {
  setShowModalAdd: (v: boolean) => void;
  refreshEquip: () => void;
  status: any[];
  access: any;
  prefillData?: Record<string, any>;
}

export default function AddEquipment({ setShowModalAdd, refreshEquip, status, access, prefillData }: Props) {
  const p = prefillData ?? {};
  const { enqueueSnackbar } = useSnackbar();

  const [users,        setUsers]        = useState<any[]>([]);
  const [locationAll,  setLocationAll]  = useState<any[]>([]);
  const [categoryAll,  setCategoryAll]  = useState<any[]>([]);

  const [name,         setName]         = useState(p.item_name ?? '');
  const [manufacturer, setManufacturer] = useState(p.manufacturer ?? '');
  const [model,        setModel]        = useState(p.model ?? '');
  const [serial,       setSerial]       = useState(''); // never prefill
  const [inventoryID,  setInventoryID]  = useState('');
  const [cost,         setCost]         = useState(p.cost ? String(p.cost) : '');
  const [notes,        setNotes]        = useState(p.notes ?? '');

  const [categoryEQ,  setCategoryEQ]  = useState<number | null>(p.categoryEquipmentId ?? null);
  const [location,    setLocation]    = useState<number | null>(p.locationId ?? null);
  const [user,        setUser]        = useState<number | null>(p.responsibleUserId ?? null);
  const [statusQ,     setStatusQ]     = useState<number | null>(p.statusId ?? null);

  const [purchDate,   setPurchDate]   = useState<Date | null>(p.purchase_date ? new Date(p.purchase_date) : null);
  const [warDate,     setWarDate]     = useState<Date | null>(p.warranty ? new Date(p.warranty) : null);
  const [invDate,     setInvDate]     = useState<Date | null>(null);
  const [invUser,     setInvUser]     = useState<number | null>(null);

  const [patTest,     setPatTest]     = useState<boolean>(p.pat_test ?? false);
  const [patDue,      setPatDue]      = useState<number>(p.pat_due ?? 0);
  const [patDate,     setPatDate]     = useState<Date | null>(null);
  const [patUser,     setPatUser]     = useState<number | null>(null);

  const [capitalised, setCapitalised] = useState<boolean>(p.capitalised ?? false);
  const [method,      setMethod]      = useState(p.depreciation_method ?? '');
  const [months,      setMonths]      = useState(p.depreciation_months ? String(p.depreciation_months) : '');
  const [strand,      setStrand]      = useState(p.funding_strand ?? '');
  const [netValue,    setNetValue]    = useState('');
  const [rate,        setRate]        = useState('');
  const [addition,    setAddition]    = useState('');
  const [disposed,    setDisposed]    = useState('');
  const [details,     setDetails]     = useState('');
  const [startDate,   setStartDate]   = useState<Date | null>(null);
  const [rental,      setRental]      = useState<boolean>(p.rental_opportunity ?? false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: u }, { data: l }, { data: c }] = await Promise.all([
          axios.get('users'), axios.get('location'), axios.get('category-equipment'),
        ]);
        setUsers(u); setLocationAll(l); setCategoryAll(c);
      } catch {}
    })();
  }, []);

  const patDueLabel = () => {
    if (!patDate || !patDue) return '';
    const d = new Date(patDate);
    d.setMonth(d.getMonth() + patDue);
    return d.toLocaleDateString('en-GB');
  };

  const handleSave = async () => {
    if (!categoryEQ) { enqueueSnackbar('Category required', { variant: 'error' }); return; }
    if (!inventoryID) { enqueueSnackbar('Inventory ID required', { variant: 'error' }); return; }
    if (!name.trim()) { enqueueSnackbar('Name required', { variant: 'error' }); return; }
    if (patTest && !patDate) { enqueueSnackbar('PAT date required', { variant: 'error' }); return; }

    try {
      const body: any = {
        item_name: name, manufacturer, model, serial_number: serial,
        cost: cost ? Number(cost) : null, notes,
        locationId: location, categoryEquipmentId: categoryEQ,
        responsibleUserId: user, statusId: statusQ,
        purchase_date: purchDate, warranty: warDate,
        inventory_id: inventoryID, rental_opportunity: rental,
        capitalised,
        pat_test: patTest,
        pat_date: patTest ? patDate : null,
        pat_due: patTest ? patDue : 0,
        patUserId: patTest ? patUser : null,
      };
      if (access.equipment_inventory_mode) {
        body.inventory_date = invDate;
        body.inventoryUserId = invUser;
      }
      if (capitalised) {
        body.depreciation_method = method;
        body.funding_strand = strand;
        body.depreciation_start_date = startDate;
        body.depreciation_months = Number(months) || null;
        body.depreciation_rate = rate ? Number(rate) : null;
        body.additions = addition || null;
        body.net_book_value = netValue || null;
        body.disposed_cost = disposed || null;
        body.invoice_details = details || null;
      }
      await axios.post('equipment/add', body);
      setShowModalAdd(false);
      refreshEquip();
      enqueueSnackbar('Created successfully', { variant: 'success' });
    } catch {
      enqueueSnackbar('Error creating', { variant: 'error' });
    }
  };

  const inp = (
    val: string, set: (v: string) => void,
    placeholder = '', type = 'text', readonly = false
  ) => (
    <input
      type={type} value={val} readOnly={readonly}
      placeholder={placeholder}
      onChange={e => !readonly && set(e.target.value)}
      className={`eq-di${readonly ? ' eq-di-readonly' : ''}`}
    />
  );

  const sel = (val: number | string | null, set: (v: any) => void, opts: { id: any; label: string }[], title = 'Select') => (
    <select
      value={val ?? 'none'}
      onChange={e => set(e.target.value === 'none' ? null : Number(e.target.value))}
      className="eq-di"
      title={title}
      style={{ appearance: 'none', cursor: 'pointer' }}
    >
      <option value="none">— none —</option>
      {opts.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
    </select>
  );

  const dp = (val: Date | null, set: (d: Date) => void, disabled = false) => (
    <CalendarPicker
      mode="single"
      variant="bare"
      className="eq-di"
      value={val}
      onChange={(d: any) => d && set(d)}
      displayFormat="DD/MM/YYYY"
      disabled={disabled}
    />
  );

  const isDuplicate = !!prefillData;

  return (
    <div className="eq-drawer">
      {/* Header */}
      <div className="eq-dh">
        <div className="eq-dh-icon" style={{ background: 'rgba(245,158,11,.12)' }}>
          {isDuplicate ? '⧉' : '🖥'}
        </div>
        <div>
          <div className="eq-dh-title">{isDuplicate ? `Duplicate: ${p.item_name ?? ''}` : t('AddEqp')}</div>
          <div className="eq-dh-sub">{isDuplicate ? 'New record based on existing item' : 'New equipment record'}</div>
        </div>
        <button type="button" className="eq-dh-close" onClick={() => setShowModalAdd(false)}>✕</button>
      </div>

      {/* Body */}
      <div className="eq-db">

        {/* Asset details */}
        <div className="eq-ds">
          <div className="eq-ds-title">Asset details</div>
          <div className="eq-dg2">
            <div className="eq-df"><label className="eq-dl">{t('Name')}</label>{inp(name, setName, t('Name'))}</div>
            <div className="eq-df"><label className="eq-dl">{t('InventoryID')}</label>{inp(inventoryID, setInventoryID, t('InventoryID'))}</div>
            <div className="eq-df"><label className="eq-dl">{t('Manufacturer')}</label>{inp(manufacturer, setManufacturer, t('Manufacturer'))}</div>
            <div className="eq-df"><label className="eq-dl">{t('Model')}</label>{inp(model, setModel, t('Model'))}</div>
            <div className="eq-df"><label className="eq-dl">{t('SerialNumber')}</label>{inp(serial, setSerial, t('SerialNumber'))}</div>
            <div className="eq-df"><label className="eq-dl">{t('Cost')} (£)</label>{inp(cost, setCost, '0.00', 'text')}</div>
          </div>
        </div>

        {/* Location & assignment */}
        <div className="eq-ds">
          <div className="eq-ds-title">Location &amp; assignment</div>
          <div className="eq-dg2">
            <div className="eq-df"><label className="eq-dl">{t('Category')}</label>
              <CategoryTaskPicker
                nodes={categoryAll.map((c: any) => ({ id: c.id, name: c.name }))}
                selectedCategoryId={categoryEQ != null ? [categoryEQ] : []}
                categoryName={[]}
                onCategorySelect={(id) => setCategoryEQ(id === -1 ? null : id)}
                selectedTasks={[]}
                setSelectedTasks={() => {}}
                taskOrTasks={false}
                categoryOnly
                block
              />
            </div>
            <div className="eq-df"><label className="eq-dl">{t('Location')}</label>
              {sel(location, setLocation, locationAll.map(l => ({ id: l.id, label: l.name })))}
            </div>
            <div className="eq-df"><label className="eq-dl">{t('ResponsibleForItem')}</label>
              {sel(user, setUser, users.map(u => ({ id: u.id, label: `${u.first_name} ${u.last_name}` })))}
            </div>
            <div className="eq-df"><label className="eq-dl">{t('Status')}</label>
              {sel(statusQ, setStatusQ, status.map(s => ({ id: s.id, label: s.name })))}
            </div>
          </div>
          <div className="eq-df" style={{ marginTop: 4 }}>
            <label className="eq-df-check">
              <input type="checkbox" checked={rental} onChange={e => setRental(e.target.checked)} />
              <span style={{ fontSize: 13, color: 'var(--text-color)' }}>{t('RentalOpportunity')}</span>
            </label>
          </div>
        </div>

        {/* PAT Test */}
        {access.equipment_pat_test && (
          <div className="eq-ds">
            <div className="eq-ds-title">PAT Test</div>
            <div className="eq-df" style={{ marginBottom: 10 }}>
              <label className="eq-df-check">
                <input type="checkbox" checked={patTest} onChange={e => setPatTest(e.target.checked)} />
                <span style={{ fontSize: 13, color: 'var(--text-color)' }}>PAT test required</span>
              </label>
            </div>
            {patTest && (
              <div className="eq-dg2">
                <div className="eq-df"><label className="eq-dl">{t('PATTestDate')}</label>{dp(patDate, setPatDate)}</div>
                <div className="eq-df">
                  <label className="eq-dl">PAT due (calculated)</label>
                  <input type="text" readOnly value={patDueLabel()} className="eq-di" style={{ opacity: .5 }} />
                </div>
                <div className="eq-df">
                  <label className="eq-dl">Interval (months)</label>
                  <input type="number" min={0} max={60} value={patDue}
                    onChange={e => setPatDue(Number(e.target.value))} className="eq-di" />
                </div>
                <div className="eq-df"><label className="eq-dl">{t('ResponsibleForPAT')}</label>
                  {sel(patUser, setPatUser, users.map(u => ({ id: u.id, label: `${u.first_name} ${u.last_name}` })))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Inventory */}
        <div className="eq-ds">
          <div className="eq-ds-title">Inventory</div>
          <div className="eq-dg2">
            <div className="eq-df"><label className="eq-dl">{t('PurchaseDate')}</label>{dp(purchDate, setPurchDate)}</div>
            <div className="eq-df"><label className="eq-dl">{t('WarrantyDue')}</label>{dp(warDate, setWarDate)}</div>
            {access.equipment_inventory_mode && <>
              <div className="eq-df"><label className="eq-dl">{t('InventoryDate')}</label>{dp(invDate, setInvDate)}</div>
              <div className="eq-df"><label className="eq-dl">{t('InventoryUser')}</label>
                {sel(invUser, setInvUser, users.map(u => ({ id: u.id, label: `${u.first_name} ${u.last_name}` })))}
              </div>
            </>}
          </div>
        </div>

        {/* Capitalisation */}
        <div className="eq-ds">
          <div className="eq-ds-title">Capitalisation</div>
          <div className="eq-df" style={{ marginBottom: 10 }}>
            <label className="eq-df-check">
              <input type="checkbox" checked={capitalised} onChange={e => setCapitalised(e.target.checked)} />
              <span style={{ fontSize: 13, color: 'var(--text-color)' }}>{t('Capitalised')}</span>
            </label>
          </div>
          {capitalised && (
            <div className="eq-dg2">
              <div className="eq-df">
                <label className="eq-dl">{t('Method')}</label>
                <select value={method} onChange={e => setMethod(e.target.value)} className="eq-di" title={t('Method')} style={{ appearance: 'none' }}>
                  <option value="">— none —</option>
                  <option value="straight line">Straight-line</option>
                  <option value="reducing balance">Reducing balance</option>
                </select>
              </div>
              <div className="eq-df"><label className="eq-dl">{t('Months')}</label>{inp(months, setMonths, '60', 'text')}</div>
              <div className="eq-df"><label className="eq-dl">{t('Stand')}</label>{inp(strand, setStrand)}</div>
              <div className="eq-df"><label className="eq-dl">{t('StartDate')}</label>{dp(startDate, setStartDate)}</div>
              <div className="eq-df"><label className="eq-dl">{t('Rate')}</label>{inp(rate, setRate, '0.00')}</div>
              <div className="eq-df"><label className="eq-dl">{t('NetBookValue')}</label>{inp(netValue, setNetValue, '0.00')}</div>
              <div className="eq-df"><label className="eq-dl">{t('Additions')}</label>{inp(addition, setAddition, '0.00')}</div>
              <div className="eq-df"><label className="eq-dl">{t('DisposedCost')}</label>{inp(disposed, setDisposed, '0.00')}</div>
              <div className="eq-df" style={{ gridColumn: '1/-1' }}><label className="eq-dl">{t('InvoiceDetails')}</label>{inp(details, setDetails)}</div>
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="eq-ds">
          <div className="eq-ds-title">Notes</div>
          <div className="eq-dg1">
            <div className="eq-df">
              <textarea value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Additional notes…" className="eq-dta" />
            </div>
          </div>
        </div>

      </div>

      {/* Footer */}
      <div className="eq-foot">
        <div className="eq-foot-l">
          {isDuplicate ? `Duplicating: ${p.item_name ?? ''}` : 'New item'}
        </div>
        <div className="eq-foot-r">
          <button type="button" className="eq-cancel" onClick={() => setShowModalAdd(false)}>{t('Cancel')}</button>
          <button type="button" className="eq-save" onClick={handleSave}>{t('Change')}</button>
        </div>
      </div>
    </div>
  );
}
